import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  useMediaQuery,
  useTheme,
  Alert,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
} from '@mui/material';
import { ExpandMore, ArrowBack } from '@mui/icons-material';
import { api } from '../../services/api';
import { DailyCollection, computeRemainingBalance, roundCurrency, fmtCurrency, formatDueDateDisplay } from './types';

interface SaleInstallment {
  installment_number: number;
  due_date: string;
  current_amount: number;
  remaining_balance: number;
  total_paid_amount: number;
  is_paid: boolean;
  is_partial: boolean;
  is_rescheduled: boolean;
}

interface PaymentDialogProps {
  open: boolean;
  collection: DailyCollection | null;
  paymentDate?: string;  // YYYY-MM-DD; si viene se usa como fecha del pago, sino se usa la fecha actual del browser
  onClose: () => void;
  onSuccess: () => void;
}

export default function PaymentDialog({ open, collection, paymentDate, onClose, onSuccess }: PaymentDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'transfer' | 'mixed'>('cash');
  const [customAmount, setCustomAmount] = useState<number>(0);
  const [isPartial, setIsPartial] = useState(false);
  const [cashAmount, setCashAmount] = useState<number>(0);
  const [transferAmount, setTransferAmount] = useState<number>(0);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [allInstallments, setAllInstallments] = useState<SaleInstallment[]>([]);
  const [selectedInstallments, setSelectedInstallments] = useState<number[]>([]);
  const [loadingInstallments, setLoadingInstallments] = useState(false);

  const summary = useMemo(() => {
    if (!collection) return { installmentAmount: 0, totalPaid: 0, remaining: 0, paymentsCount: 0 };
    const remaining = computeRemainingBalance(collection);
    return {
      installmentAmount: roundCurrency(collection.installment_amount),
      totalPaid: roundCurrency(collection.total_paid_amount ?? 0),
      remaining,
      paymentsCount: collection.payments_count ?? 0,
    };
  }, [collection]);

  // Cuotas pendientes (no pagadas todavía), excluyendo la actual que siempre se selecciona por default
  const pendingInstallments = useMemo(
    () => allInstallments.filter((i) => !i.is_paid),
    [allInstallments],
  );

  // Reset state when dialog opens + fetch lista completa de cuotas
  useEffect(() => {
    if (open && collection) {
      const remaining = computeRemainingBalance(collection);
      const amount = remaining > 0 ? remaining : roundCurrency(collection.installment_amount);
      setPaymentMethod('cash');
      setCustomAmount(amount);
      setCashAmount(amount);
      setTransferAmount(0);
      setIsPartial(false);
      setNotes('');
      setSelectedInstallments([collection.installment_number]);
      setAllInstallments([]);
      setLoadingInstallments(true);
      api
        .get(`/sales/${collection.sale_id}/installments`)
        .then((res) => {
          setAllInstallments(res.data?.installments || []);
        })
        .catch(() => setAllInstallments([]))
        .finally(() => setLoadingInstallments(false));
    }
  }, [open, collection]);

  const toggleInstallment = (instNumber: number) => {
    if (!collection) return;
    // No permitir deseleccionar la cuota actual (anchor del cobro)
    if (instNumber === collection.installment_number) return;
    setSelectedInstallments((prev) =>
      prev.includes(instNumber) ? prev.filter((n) => n !== instNumber) : [...prev, instNumber].sort((a, b) => a - b),
    );
  };

  const multiInstallments = selectedInstallments.length;

  // Monto a cobrar por cada cuota seleccionada: la actual usa remaining (puede ser parcial),
  // las extras siempre el monto completo.
  const installmentAmountFor = (instNumber: number): number => {
    if (!collection) return 0;
    if (instNumber === collection.installment_number) {
      return isPartial ? roundCurrency(customAmount) : summary.remaining > 0 ? summary.remaining : summary.installmentAmount;
    }
    const inst = allInstallments.find((i) => i.installment_number === instNumber);
    if (inst) return roundCurrency(inst.remaining_balance > 0 ? inst.remaining_balance : inst.current_amount);
    return summary.installmentAmount;
  };

  const singleAmount = isPartial ? roundCurrency(customAmount) : summary.remaining > 0 ? summary.remaining : summary.installmentAmount;
  const effectiveAmount = useMemo(() => {
    return roundCurrency(selectedInstallments.reduce((sum, n) => sum + installmentAmountFor(n), 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstallments, isPartial, customAmount, allInstallments, summary]);

  const handleSubmit = async () => {
    if (!collection || effectiveAmount <= 0 || selectedInstallments.length === 0) return;
    setSubmitting(true);

    try {
      // Usar la fecha que el usuario está viendo en el selector (si vino como prop),
      // si no, fecha local del browser. Esto permite registrar pagos en el día correcto
      // aunque el cobrador entre al sistema al día siguiente.
      const now = new Date();
      const todayLocal = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const today = paymentDate || todayLocal;

      // Ratio para método mixto (se aplica proporcionalmente a cada cuota)
      const mixedRatio = paymentMethod === 'mixed'
        ? cashAmount / Math.max(cashAmount + transferAmount, 1)
        : 0;

      const buildBreakdown = (amount: number) => {
        if (paymentMethod === 'cash') return { cash: amount, transfer: 0 };
        if (paymentMethod === 'transfer') return { cash: 0, transfer: amount };
        const c = roundCurrency(amount * mixedRatio);
        return { cash: c, transfer: roundCurrency(amount - c) };
      };

      const sortedSelected = [...selectedInstallments].sort((a, b) => a - b);

      if (sortedSelected.length > 1) {
        // Nota compartida entre todos los Payments del batch — el endpoint
        // /payments/related filtra por "Pago de" para detectar el grupo y
        // permitir undo bulk desde el frontend.
        const batchNote = `Pago de ${sortedSelected.length} cuotas (${sortedSelected.join(', ')})`;
        const payments = sortedSelected.map((instNum) => {
          const amount = installmentAmountFor(instNum);
          const { cash, transfer } = buildBreakdown(amount);
          return {
            sale_id: collection.sale_id,
            installment_number: instNum,
            amount,
            payment_method: paymentMethod,
            cash_amount: cash,
            transfer_amount: transfer,
            payment_date: today,
            notes: notes ? `${batchNote} - ${notes}` : batchNote,
          };
        });
        await api.post('/payments/multi', { payments });
      } else {
        const onlyInst = sortedSelected[0];
        const amount = installmentAmountFor(onlyInst);
        const { cash, transfer } = buildBreakdown(amount);
        await api.post('/payments', {
          sale_id: collection.sale_id,
          installment_number: onlyInst,
          amount,
          payment_method: paymentMethod,
          cash_amount: cash,
          transfer_amount: transfer,
          payment_date: today,
          notes: notes || undefined,
        });
      }

      onSuccess();
    } catch (error) {
      console.error('Error submitting payment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!collection) return null;

  const methodLabel = paymentMethod === 'cash' ? 'Efectivo' : paymentMethod === 'transfer' ? 'Transferencia' : 'Mixto';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <Box sx={{ p: { xs: 2, sm: 3 }, height: isMobile ? '100%' : 'auto', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        {isMobile && (
          <Button
            startIcon={<ArrowBack />}
            onClick={onClose}
            sx={{ alignSelf: 'flex-start', mb: 1, color: 'text.secondary', minHeight: 44 }}
          >
            Volver
          </Button>
        )}
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Registrar Pago
        </Typography>
        <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: 'text.primary', mt: 0.5 }}>
          {collection.customer_surname} {collection.customer_name}
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 0.25 }}>
          {collection.notes?.startsWith('Productos: ') ? collection.notes.replace('Productos: ', '') : collection.product_name} - Cuota {collection.installment_number}/{collection.total_installments}
        </Typography>

        {/* Payment summary info */}
        {summary.paymentsCount > 0 && (
          <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
              Monto cuota: {fmtCurrency(summary.installmentAmount)} | Pagado: {fmtCurrency(summary.totalPaid)} | <strong>Saldo: {fmtCurrency(summary.remaining)}</strong>
            </Typography>
          </Box>
        )}

        {/* Multi installment info */}
        {multiInstallments > 1 && (
          <Alert severity="info" sx={{ mt: 1.5, fontSize: '0.8rem' }}>
            Pagando {multiInstallments} cuotas ({[...selectedInstallments].sort((a, b) => a - b).join(', ')}) = {fmtCurrency(effectiveAmount)}
          </Alert>
        )}

        {/* Big confirm button - default action */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={submitting || effectiveAmount <= 0}
          sx={{
            mt: 2.5,
            py: { xs: 2.5, sm: 2 },
            fontSize: { xs: '1.05rem', sm: '1rem' },
            fontWeight: 700,
            background: 'linear-gradient(135deg, #10B981, #059669)',
            '&:hover': { background: 'linear-gradient(135deg, #059669, #047857)' },
            borderRadius: 2,
            minHeight: { xs: 56, sm: 48 },
          }}
        >
          {submitting ? 'Procesando...' : `Confirmar Cobro ${fmtCurrency(effectiveAmount)} en ${methodLabel}`}
        </Button>

        {/* Expandable sections for less common options */}
        <Box sx={{ mt: 2 }}>
          {/* Selector de cuotas (multi-select) */}
          {pendingInstallments.length > 1 && (
            <Accordion
              disableGutters
              elevation={0}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', '&:before': { display: 'none' }, mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Seleccionar cuotas a cobrar</Typography>
                  {multiInstallments > 1 && (
                    <Chip size="small" color="primary" label={`${multiInstallments} seleccionadas`} />
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Tildá las cuotas que vas a cobrar. La cuota actual no se puede deseleccionar.
                </Typography>
                {loadingInstallments ? (
                  <Typography variant="caption" color="text.secondary">Cargando cuotas…</Typography>
                ) : (
                  <List dense disablePadding sx={{ maxHeight: 280, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    {pendingInstallments.map((inst) => {
                      const checked = selectedInstallments.includes(inst.installment_number);
                      const isCurrent = inst.installment_number === collection.installment_number;
                      const dueLabel = formatDueDateDisplay(inst.due_date);
                      const amountLabel = fmtCurrency(inst.remaining_balance > 0 ? inst.remaining_balance : inst.current_amount);
                      return (
                        <ListItem key={inst.installment_number} disablePadding>
                          <ListItemButton
                            dense
                            onClick={() => toggleInstallment(inst.installment_number)}
                            disabled={isCurrent}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <Checkbox
                                edge="start"
                                checked={checked}
                                tabIndex={-1}
                                disableRipple
                                disabled={isCurrent}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                    Cuota {inst.installment_number}/{collection.total_installments}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                    {dueLabel}
                                  </Typography>
                                  {inst.is_partial && (
                                    <Chip size="small" color="info" label="Parcial" sx={{ height: 18, fontSize: '0.65rem' }} />
                                  )}
                                  {inst.is_rescheduled && (
                                    <Chip size="small" color="default" label="Reprog." sx={{ height: 18, fontSize: '0.65rem' }} />
                                  )}
                                  {isCurrent && (
                                    <Chip size="small" color="primary" label="Actual" sx={{ height: 18, fontSize: '0.65rem' }} />
                                  )}
                                </Stack>
                              }
                              secondary={amountLabel}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          <Accordion
            disableGutters
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', '&:before': { display: 'none' }, mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Cambiar forma de pago</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControl fullWidth size="small">
                <InputLabel>Metodo de Pago</InputLabel>
                <Select
                  value={paymentMethod}
                  onChange={(e) => {
                    const method = e.target.value as 'cash' | 'transfer' | 'mixed';
                    setPaymentMethod(method);
                    if (method === 'cash') {
                      setCashAmount(singleAmount);
                      setTransferAmount(0);
                    } else if (method === 'transfer') {
                      setCashAmount(0);
                      setTransferAmount(singleAmount);
                    } else {
                      setCashAmount(singleAmount);
                      setTransferAmount(0);
                    }
                  }}
                  label="Metodo de Pago"
                >
                  <MenuItem value="cash">Efectivo</MenuItem>
                  <MenuItem value="transfer">Transferencia</MenuItem>
                  <MenuItem value="mixed">Mixto (Efectivo + Transferencia)</MenuItem>
                </Select>
              </FormControl>
              {paymentMethod === 'mixed' && (
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Efectivo"
                      type="number"
                      value={cashAmount}
                      onChange={(e) => {
                        const val = Math.max(parseFloat(e.target.value) || 0, 0);
                        const limited = Math.min(val, singleAmount);
                        setCashAmount(roundCurrency(limited));
                        setTransferAmount(roundCurrency(Math.max(singleAmount - limited, 0)));
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Transferencia"
                      type="number"
                      value={transferAmount}
                      onChange={(e) => {
                        const val = Math.max(parseFloat(e.target.value) || 0, 0);
                        const limited = Math.min(val, singleAmount);
                        setTransferAmount(roundCurrency(limited));
                        setCashAmount(roundCurrency(Math.max(singleAmount - limited, 0)));
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>
                </Grid>
              )}
            </AccordionDetails>
          </Accordion>

          <Accordion
            disableGutters
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', '&:before': { display: 'none' }, mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Cobro parcial</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Button
                  size="small"
                  variant={!isPartial ? 'contained' : 'outlined'}
                  onClick={() => setIsPartial(false)}
                >
                  Total
                </Button>
                <Button
                  size="small"
                  variant={isPartial ? 'contained' : 'outlined'}
                  onClick={() => {
                    setIsPartial(true);
                    // Si había múltiples seleccionadas, reducir a la actual (parcial solo aplica a 1)
                    if (collection) setSelectedInstallments([collection.installment_number]);
                    setCustomAmount(summary.remaining > 0 ? summary.remaining : summary.installmentAmount);
                  }}
                >
                  Parcial
                </Button>
              </Box>
              {isPartial && (
                <TextField
                  fullWidth
                  size="small"
                  label="Monto a cobrar"
                  type="number"
                  value={customAmount}
                  onChange={(e) => {
                    const val = Math.max(parseFloat(e.target.value) || 0, 0);
                    setCustomAmount(roundCurrency(val));
                    if (paymentMethod === 'cash') {
                      setCashAmount(roundCurrency(val));
                    } else if (paymentMethod === 'transfer') {
                      setTransferAmount(roundCurrency(val));
                    }
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              )}
            </AccordionDetails>
          </Accordion>

          <Accordion
            disableGutters
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Agregar nota</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                fullWidth
                size="small"
                label="Notas (opcional)"
                multiline
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Spacer on mobile to push cancel to bottom */}
        {isMobile && <Box sx={{ flex: 1 }} />}

        {/* Cancel button */}
        <Button fullWidth onClick={onClose} sx={{ mt: 1.5, color: 'text.secondary', minHeight: { xs: 48, sm: 36 } }}>
          Cancelar
        </Button>
      </Box>
    </Dialog>
  );
}
